// src/encounter/lib/smart-phrase.ts
//
// Saved phrases (the provider's MACROS, the records the Settings → Macro tab manages) in the
// encounter's Quill notes — the trigger, the list, and what an insertion writes. Pure functions,
// so every rule is testable without an editor; `smart-phrase-editor.ts` applies them to Quill.
// Spec: docs/encounter-alignment/SPEC-smart-phrases-and-template-filling.md §4.1.
//
//   TRIGGER  `.` at the start of a word, then the phrase's trigger. The trigger is DERIVED from the
//            stored `macroKey` (lower-case, spaces → `-`: "Individual Session" → `.individual-session`);
//            stored keys never change. "Stable." (a full stop after a word) never triggers.
//   LIST     type-ahead on trigger and title; own phrases, then shared with me, then org-wide.
//            Duplicate triggers are all listed (with their scope) and never auto-picked.
//   INSERT   the stored body with its PIECE DEFINITIONS turned into fresh occurrences
//            (`note-pieces.ts`, R1). A body with block formatting (lists, headings, checklists)
//            goes in as its own block; a plain body goes in inline, minus Quill's terminal newline.
//
// A phrase writes narrative only (rule 3 / R4): never a diagnosis, an order, a charge, a signature
// or a structured template answer.

import { parseNoteOps, type DeltaOp } from './note-delta';
import {
  PIECE_ATTR,
  PIECE_EMBED,
  parsePiece,
  pieceDisplay,
  toDefinition,
  toOccurrence,
  type ChartQuery,
  type ChartSnapshot,
} from './note-pieces';

/** A `.trigger` being typed at the cursor: `start` is the index of the dot. */
export interface PhraseTrigger {
  start: number;
  query: string;
}

// A dot at the start of the text or after whitespace / an opening bracket, then the trigger so far.
const TRIGGER_RE = /(?:^|[\s(])\.([a-z0-9][a-z0-9_-]{0,59})$/i;

export function phraseTriggerAt(text: string, cursor: number): PhraseTrigger | undefined {
  const m = TRIGGER_RE.exec(text.slice(0, cursor));
  if (!m) return undefined;
  const query = m[1];
  return { start: cursor - query.length - 1, query };
}

/** `macroKey` → its trigger: lower-case, whitespace runs → `-`, a leading dot dropped. */
export function triggerOf(macroKey: string): string {
  return macroKey.trim().replace(/^\./, '').toLowerCase().replace(/\s+/g, '-');
}

/**
 * L8 / F5 (live dev, 6 Oct) · frame v2: a phrase's HUMAN name, from its stored key. The dev list showed dotted
 * ids (".copy:-goal", ".clinical-telephone-call") as names. Leading dots go, separators (`:` `-` `_` `.` `/`)
 * become spaces, runs collapse, and the first letter is capitalised: ".copy:-goal" → "Copy goal". The stored
 * key is never changed (Settings owns it); a key with nothing readable keeps its trimmed text.
 */
export function phraseName(macroKey: string): string {
  const words = macroKey
    .trim()
    .replace(/^[.\s]+/, '')
    .replace(/[:\-_./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!words) return macroKey.trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A typeable `.trigger` for a phrase (frame v2 note lines): the derived trigger with every character a typed
 *  trigger cannot hold (`:` and the like) folded to `-`. ".copy:-goal" → "copy-goal". */
export function typeableTrigger(trigger: string): string {
  return trigger.replace(/[^a-z0-9_-]+/gi, '-').replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
}

export type PhraseScope = 'own' | 'shared' | 'global';

/** A saved phrase as the note needs it. */
export interface NoteMacro {
  id: string;
  /** The stored `macroKey` — the phrase's title. */
  key: string;
  /** `.trigger` without the dot (`triggerOf(key)`). */
  trigger: string;
  /** Stringified Delta ops (`macroValue`) — the body, piece definitions included. */
  value: string;
  /** Plain-text rendering (`macroValueFormatted`) — list preview only. */
  preview: string;
  scope: PhraseScope;
}

const SCOPE_RANK: Record<PhraseScope, number> = { own: 0, shared: 1, global: 2 };

/**
 * L8 / F5 · frame v2: one row per phrase. Drops a repeated row (same id) and an exact duplicate (same human
 * name and the same stored body), keeping the closest scope (own → shared → org-wide) and, within that, the
 * first. The dev catalogue held ".clinical-telephone-call" ×2 and ".copy:-goal" ×3. Rows that differ in body
 * are both kept — same name, different text is a real choice, not a duplicate. Nothing is deleted from the org's
 * data: the list only shows each phrase once.
 */
export function dedupeMacros(macros: readonly NoteMacro[]): NoteMacro[] {
  const byId = new Set<string>();
  const kept = new Map<string, NoteMacro>();
  const order: string[] = [];
  for (const m of macros) {
    if (byId.has(m.id)) continue;
    byId.add(m.id);
    const key = `${phraseName(m.key).toLowerCase()}\u0000${m.value}`;
    const prev = kept.get(key);
    if (!prev) {
      kept.set(key, m);
      order.push(key);
    } else if (SCOPE_RANK[m.scope] < SCOPE_RANK[prev.scope]) {
      kept.set(key, m);
    }
  }
  return order.map((k) => kept.get(k) as NoteMacro);
}

/**
 * Phrases matching the typed query: trigger prefix first, then trigger-or-title contains; within
 * each, own → shared → global, then alphabetical. An empty query lists every phrase.
 */
export function matchMacros(macros: NoteMacro[], query: string, limit = 8): NoteMacro[] {
  const q = query.trim().replace(/^\./, '').toLowerCase();
  const ranked: Array<{ macro: NoteMacro; rank: number }> = [];
  for (const macro of macros) {
    const title = macro.key.toLowerCase();
    if (!q || macro.trigger.startsWith(q)) ranked.push({ macro, rank: 0 });
    else if (macro.trigger.includes(q) || title.includes(q)) ranked.push({ macro, rank: 1 });
  }
  ranked.sort(
    (a, b) =>
      a.rank - b.rank ||
      SCOPE_RANK[a.macro.scope] - SCOPE_RANK[b.macro.scope] ||
      a.macro.trigger.localeCompare(b.macro.trigger),
  );
  return ranked.slice(0, limit).map((r) => r.macro);
}

// ─── Where a phrase belongs ──────────────────────────────────────────────────────────────────────

/**
 * Phrase-level metadata, carried like the spec's piece index (§6.4): an attribute on the body's
 * terminal newline, `cps-phrase` = `{"v":1,"editors":[…]}`. `editors` names the notes the phrase is
 * written for (`ap-narrative`, `chief-complaint-note`, …). flutter_quill ignores the unknown
 * attribute; it is never inserted into a note.
 */
export const PHRASE_ATTR = 'cps-phrase';

/** The note editors a phrase body declares it is for; undefined = it declares none (offered anywhere). */
export function phraseEditors(storedValue: string): string[] | undefined {
  for (const op of parseNoteOps(storedValue)) {
    const raw = op.attributes?.[PHRASE_ATTR];
    if (raw === undefined) continue;
    try {
      const meta: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const editors = (meta as { v?: unknown; editors?: unknown })?.editors;
      if ((meta as { v?: unknown })?.v === 1 && Array.isArray(editors) && editors.every((e) => typeof e === 'string')) {
        return editors as string[];
      }
    } catch {
      // unreadable metadata declares nothing
    }
  }
  return undefined;
}

/** PROD-3149: the phrase's own title, when its body carries one (`cps-phrase` `title`, written by the library import);
 *  undefined when it has none (every legacy macro, and a library imported before the title was kept). */
export function phraseTitle(storedValue: string): string | undefined {
  for (const op of parseNoteOps(storedValue)) {
    const raw = op.attributes?.[PHRASE_ATTR];
    if (raw === undefined) continue;
    try {
      const meta: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const title = (meta as { v?: unknown; title?: unknown })?.title;
      if ((meta as { v?: unknown })?.v === 1 && typeof title === 'string' && title.trim()) return title.trim();
    } catch {
      // unreadable metadata declares nothing
    }
  }
  return undefined;
}

/** How a phrase is NAMED in a menu (PROD-3149, Andrew: rows read as codes): its own title, else its key made readable. */
export function phraseLabel(macro: Pick<NoteMacro, 'key' | 'value'>): string {
  return phraseTitle(macro.value) ?? phraseName(macro.key);
}

/**
 * Does a stored phrase carry anything smart — a piece or phrase metadata? An editor that has not
 * registered these attributes (the Settings macro editor) would strip them on save, so such a
 * phrase must open read-only there. Any presence counts, readable or not.
 */
export function carriesSmartParts(storedValue: string | null | undefined): boolean {
  return parseNoteOps(storedValue).some((op) => !!op.attributes && (PIECE_ATTR in op.attributes || PHRASE_ATTR in op.attributes));
}

/** Offered in `editor`: the phrase names it, or names no editor at all (every legacy macro). */
export function isOfferedIn(storedValue: string, editor: string): boolean {
  const editors = phraseEditors(storedValue);
  return !editors || editors.includes(editor);
}

/** How many piece DEFINITIONS a stored phrase body carries (runs with `cps-piece`, one per piece). */
export function definitionPieceCount(storedValue: string): number {
  const ops = parseNoteOps(storedValue);
  let n = 0;
  for (let i = 0; i < ops.length; i++) {
    const raw = ops[i].attributes?.[PIECE_ATTR];
    if (raw === undefined) continue;
    n++;
    while (i + 1 < ops.length && ops[i + 1].attributes?.[PIECE_ATTR] === raw) i++;
  }
  return n;
}

// ─── Insertion ───────────────────────────────────────────────────────────────────────────────────

const BLOCK_ATTRS = ['list', 'header', 'blockquote', 'code-block', 'align', 'indent', 'direction'];

function isBlockLine(op: DeltaOp): boolean {
  return typeof op.insert === 'string' && op.insert.includes('\n') && BLOCK_ATTRS.some((a) => op.attributes?.[a] != null);
}

/** Length of a run of ops in Quill index units (a string counts its characters, an embed counts 1). */
export function opsLength(ops: DeltaOp[]): number {
  return ops.reduce((n, op) => n + (typeof op.insert === 'string' ? op.insert.length : op.insert ? 1 : 0), 0);
}

export interface PhraseInsertion {
  ops: DeltaOp[];
  /** The body carries block formatting → it must start on its own line. */
  block: boolean;
}

/**
 * The editor ops a saved phrase inserts. Each piece DEFINITION run (a text run carrying
 * `cps-piece`, consecutive runs with the same value being one piece) whose text is its open display
 * (`[not completed: <label>]`) becomes a fresh occurrence embed. A malformed definition, or one
 * whose text was edited elsewhere, stays as its text.
 */
export function phraseInsertOps(
  storedValue: string,
  resolveChart: (q: ChartQuery) => ChartSnapshot,
): PhraseInsertion {
  // Phrase metadata describes the phrase, not the note: it is never inserted.
  const stored = parseNoteOps(storedValue).map((op) => {
    if (!op.attributes || !(PHRASE_ATTR in op.attributes)) return op;
    const attributes = { ...op.attributes };
    delete attributes[PHRASE_ATTR];
    return Object.keys(attributes).length ? { ...op, attributes } : { insert: op.insert };
  });
  const ops: DeltaOp[] = [];
  for (let i = 0; i < stored.length; i++) {
    const op = stored[i];
    const raw = op.attributes?.[PIECE_ATTR];
    if (raw === undefined || typeof op.insert !== 'string') {
      ops.push(op);
      continue;
    }
    let text = op.insert;
    while (i + 1 < stored.length && stored[i + 1].attributes?.[PIECE_ATTR] === raw && typeof stored[i + 1].insert === 'string') {
      text += stored[++i].insert as string;
    }
    const def = parsePiece(raw);
    // Same checksum rule as a saved note (§6.5): a definition run whose text is no longer its open
    // display was edited somewhere that does not know pieces, so the edit wins as plain text.
    if (def && text === pieceDisplay(toDefinition(def))) ops.push({ insert: { [PIECE_EMBED]: toOccurrence(def, resolveChart) } });
    else ops.push({ insert: text });
  }

  const block = ops.some(isBlockLine);
  const last = ops[ops.length - 1];
  if (!block && last && typeof last.insert === 'string' && !last.attributes && last.insert.endsWith('\n')) {
    const trimmed = last.insert.slice(0, -1);
    return { ops: trimmed ? [...ops.slice(0, -1), { ...last, insert: trimmed }] : ops.slice(0, -1), block };
  }
  return { ops, block };
}
